import { Box, Button, Typography, getContrastRatio } from "@mui/material"
import { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { LoadingButton } from "@mui/lab"
import CustomAlert from "./CustomAlert"
import CheckIcon from "../../../components/icons/CheckIcon"
import { updateProjectState } from "../../../redux/projectSlice"
import { setSnackbar } from "../../../redux/messageSlice"
import { removeOrganisationProjectMemberApi } from "../../../services/api"
import { showSnackBar, generateLighterContrastColor } from "../../../utils/commonServices"
import useCustomTheme from "../../../theme/useCustomTheme"
import useOrganisationProjectActions from "../useOrganisationProjectActions"

const OverrideConfirmationPopup = () => {
    const dispatch = useDispatch()
    const commonReducer = useSelector((state) => state.root.commonReducer)
    const projectReducer = useSelector((state) => state.projectReducer)
    const [theme] = useCustomTheme()
    const [loading, setLoading] = useState(false)
    const { getProjectMembersList } = useOrganisationProjectActions()

    const member = projectReducer.override_target_member
    const memberName = member?.name || 'this editor'

    const handleClose = () => {
        dispatch(updateProjectState({
            override_confirmation_popup: false,
            override_target_member: null,
            loading_state: 'noloading'
        }))
    }

    const handleConfirm = () => {
        setLoading(true)
        const payload = {
            event_id: commonReducer?.production_event_id,
            internal_user_id: member?.internal_user_id,
            organisation_id: commonReducer?.selected_organisation?.id
        }
        removeOrganisationProjectMemberApi(payload)
            .then((response) => {
                setLoading(false)
                if (response?.data?.http_code === 200) {
                    dispatch(setSnackbar({ type: 'success', flag: true, message: response?.data?.message }))
                    getProjectMembersList({ event_id: commonReducer?.production_event_id })
                    handleClose()
                }
            })
            .catch((error) => {
                setLoading(false)
                if (error) {
                    const errorObject = error?.response?.data
                    showSnackBar(dispatch, errorObject)
                }
            })
    }

    const checkIconColor = theme.palette.primary.main === ('#00d744' || '#00D744')
        ? '#00d744'
        : getContrastRatio('#08231B', theme.palette.primary.main) > 4.5
            ? theme.palette.primary.main
            : generateLighterContrastColor(theme.palette.primary.main)

    return (
        <CustomAlert
            open={projectReducer.override_confirmation_popup}
            handleClose={handleClose}
            height='380px'
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }} px={3} pt={3} pb={2}>
                <Box flexGrow={1} pt={6}>
                    <Typography sx={{ fontWeight: 600, fontFamily: 'Rubik', fontSize: { xs: '16px', md: '18px', lg: '20px', xl: '28px' }, color: '#000' }}>
                        Remove auto-assigned editor?
                    </Typography>
                    <Typography sx={{ fontSize: { xs: '9px', md: '10px', lg: '11px', xl: '15px' }, fontFamily: 'Rubik', color: '#444', mt: 1.5, lineHeight: 1.6 }}>
                        <strong>{memberName}</strong> was assigned automatically by the system. Removing them will trigger a re-assignment in ~8 hours if no one else is assigned.
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'end' }}>
                    <Box pr={1.5}>
                        {loading ? (
                            <LoadingButton
                                sx={{
                                    '& .MuiLoadingButton-loadingIndicator': { color: theme.palette.primary.main },
                                    height: { xs: '36px', md: '45px', lg: '51px', xl: '75px' },
                                    minWidth: { xs: '36px !important', md: '45px !important', lg: '51px !important', xl: '75px !important' },
                                    backgroundColor: '#08231B'
                                }}
                                loading
                                variant="outlined"
                                disabled
                            />
                        ) : (
                            <CheckIcon
                                color={checkIconColor}
                                onClick={handleConfirm}
                                sx={{ fontSize: { xs: '40px', md: '50px', lg: '56px', xl: '82px' }, cursor: 'pointer' }}
                            />
                        )}
                    </Box>
                    <Button
                        variant="text"
                        onClick={handleClose}
                        sx={{ fontSize: { xs: '8px', md: '10px', lg: '11px', xl: '16px' }, fontWeight: 600, fontFamily: 'Rubik', textTransform: 'none', textDecoration: 'underline' }}
                    >
                        Cancel
                    </Button>
                </Box>
            </Box>
        </CustomAlert>
    )
}

export default OverrideConfirmationPopup
